import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

// ─────────────────────────────────────────────────────────────
// STYLING PASS — the Flexible/Fixed type selector previously had
// minimal visual distinction (a border-color swap on a small 2-up
// grid) despite being the FIRST decision in this flow — given more
// presence here: larger touch targets, an icon chip matching the
// treatment used on SavingsPage.jsx's GoalCard, and a checkmark
// indicator on the selected option instead of relying solely on
// border color (which is a weak signal on its own, especially in
// dark mode where border contrast is naturally lower).
// ─────────────────────────────────────────────────────────────

export default function CreateSavingsGoalPage() {
  const navigate = useNavigate();

  const [type, setType]               = useState("flexible");
  const [title, setTitle]             = useState("");
  const [targetAmount, setTarget]     = useState("");
  const [endDate, setEndDate]         = useState("");
  const [initialDeposit, setInitial]  = useState("");
  const [autoSaveOn, setAutoSaveOn]   = useState(false);
  const [autoAmount, setAutoAmount]   = useState("");
  const [autoFreq, setAutoFreq]       = useState("monthly");

  const [preview, setPreview]         = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    if (!targetAmount || !endDate || Number(targetAmount) <= 0) { setPreview(null); return; }
    const id = setTimeout(async () => {
      try {
        const r = await apiClient.post("/mtrade/savings/preview", { type, targetAmount: Number(targetAmount), endDate });
        if (r.data.status) setPreview(r.data.data);
      } catch {}
    }, 350);
    return () => clearTimeout(id);
  }, [type, targetAmount, endDate]);

  useEffect(() => {
    if (title.trim().length < 3) { setSuggestions([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await apiClient.post("/mtrade/catalog/suggest", {
          title: title.trim(),
          targetAmount: Number(targetAmount) || 0,
        });
        if (r.data.status) setSuggestions(r.data.data);
      } catch {}
    }, 400);
    return () => clearTimeout(id);
  }, [title, targetAmount]);

  const handleSuggestionSave = (p) => {
    setTarget(String(p.price));
    apiClient.post(`/mtrade/catalog/${p.id}/saved-for`).catch(() => {});
    toast.success(`Target set to ₦${Number(p.price).toLocaleString()} for ${p.name}`);
  };

  const handleSuggestionClick = (p) => {
    apiClient.post(`/mtrade/catalog/${p.id}/click`).catch(() => {});
    if (p.productUrl) window.open(p.productUrl, "_blank", "noopener");
  };

  const handleCreate = async () => {
    if (!title.trim() || !targetAmount || !endDate) {
      return toast.error("Fill in title, target amount and end date");
    }
    setLoading(true);
    try {
      const body = {
        title: title.trim(),
        type,
        targetAmount: Number(targetAmount),
        endDate,
        initialDeposit: Number(initialDeposit) || 0,
        autoSave: autoSaveOn ? { enabled: true, amount: Number(autoAmount) || 0, frequency: autoFreq } : { enabled: false },
      };
      const r = await apiClient.post("/mtrade/savings", body);
      if (r.data.status) {
        toast.success("Savings goal created");
        navigate(`/savings/${r.data.data._id}`);
      } else {
        toast.error(r.data.message || "Could not create goal");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create goal");
    } finally { setLoading(false); }
  };

  const minDate = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  // FIXED — auto-save amount is required for it to actually do
  // anything (see SavingsGoalDetailPage.jsx's new AutoSaveStatusCard —
  // built specifically because real data showed autoSave.enabled:true
  // with amount:0, a goal that LOOKS automated but moves no money).
  // Guarding it here at creation time prevents that broken state from
  // being created in the first place, rather than only surfacing it
  // after the fact on the detail page.
  const autoSaveAmountMissing = autoSaveOn && (!autoAmount || Number(autoAmount) <= 0);

  return (
    <div className="min-h-screen bg-bg pb-10">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <h1 className="text-primary font-bold text-lg font-display">New Savings Goal</h1>
        </div>
      </div>

      <div className="px-5 pt-5 pb-10 max-w-lg mx-auto space-y-5">
        {/* FIXED — type selector given real visual presence: icon
            chip (matching GoalCard's treatment), checkmark indicator
            on the selected card instead of relying on border color
            alone, taller touch target. */}
        <div className="grid grid-cols-2 gap-3">
          {[
            ["flexible", "Flexible", "ri-safe-2-line", "Withdraw anytime"],
            ["fixed", "Fixed", "ri-lock-2-line", "Locked till end date"],
          ].map(([val, label, icon, sub]) => {
            const isSelected = type === val;
            return (
              <button key={val} onClick={() => setType(val)}
                className="p-4 rounded-2xl border-2 text-left transition-all relative"
                style={{
                  borderColor: isSelected ? "var(--color-brand)" : "var(--color-border-strong)",
                  background:  isSelected ? "var(--color-brand-light)" : "var(--color-surface)",
                }}>
                {isSelected && (
                  <i className="ri-checkbox-circle-fill absolute top-3 right-3 text-base" style={{ color: "var(--color-brand)" }} />
                )}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-2.5"
                  style={{
                    background: isSelected ? "var(--color-brand)" : "var(--color-surface-hover)",
                    boxShadow: isSelected ? "inset 0 0 0 1px var(--color-brand-ring)" : "none",
                  }}
                >
                  <i className={`${icon} text-lg`} style={{ color: isSelected ? "#fff" : "var(--color-text-muted)" }} />
                </div>
                <p className="text-primary font-bold text-sm">{label}</p>
                <p className="text-faint text-[11px] mt-0.5">{sub}</p>
              </button>
            );
          })}
        </div>

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">What are you saving for?</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Rent an apartment in Lekki"
            className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint transition-colors" />
        </div>

        {suggestions.length > 0 && (
          <div className="space-y-2">
            <p className="text-faint text-[11px] font-semibold uppercase tracking-wide">You might be saving for</p>
            {suggestions.map(p => (
              <div key={p.id} className="bg-surface border border-border rounded-2xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {p.companyLogo
                    ? <img src={p.companyLogo} className="w-full h-full object-cover" alt="" />
                    : <i className="ri-store-2-line text-muted" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-primary text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-muted text-xs truncate">{p.companyName} · ₦{Number(p.price).toLocaleString()}</p>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => handleSuggestionSave(p)}
                    className="text-[10px] font-bold px-2 py-1 rounded-lg bg-brand text-on-brand">Save for this</button>
                  {p.productUrl && (
                    <button onClick={() => handleSuggestionClick(p)}
                      className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-surface-hover text-secondary">Learn more</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Target amount</label>
          <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors">
            <span className="text-muted font-bold text-lg mr-2">₦</span>
            <input type="number" value={targetAmount} onChange={e => setTarget(e.target.value)} placeholder="0.00"
              className="flex-1 py-3.5 outline-none text-primary text-lg font-bold bg-transparent placeholder:text-faint" />
          </div>
        </div>

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Target date</label>
          <input type="date" value={endDate} min={minDate} onChange={e => setEndDate(e.target.value)}
            className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand transition-colors" />
        </div>

        {preview && (
          <div className="bg-brand-light border border-brand-border rounded-2xl p-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--color-brand-text)" }}>To reach your goal, save about</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Daily", preview.recommended.daily], ["Weekly", preview.recommended.weekly], ["Monthly", preview.recommended.monthly]].map(([l, v]) => (
                <div key={l} className="bg-surface rounded-xl py-2">
                  <p className="text-faint text-[10px]">{l}</p>
                  <p className="text-primary font-bold text-sm">₦{Number(v).toLocaleString()}</p>
                </div>
              ))}
            </div>
            {preview.interestEnabled && preview.projectedInterest > 0 && (
              <p className="text-xs pt-1" style={{ color: "var(--color-brand-text)" }}>
                📈 Projected interest: <span className="font-bold">₦{Number(preview.projectedInterest).toLocaleString()}</span> at maturity
                (₦{Number(preview.projectedTotal).toLocaleString()} total)
              </p>
            )}
          </div>
        )}

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Initial deposit (optional)</label>
          <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors">
            <span className="text-muted font-bold text-lg mr-2">₦</span>
            <input type="number" value={initialDeposit} onChange={e => setInitial(e.target.value)} placeholder="0.00"
              className="flex-1 py-3.5 outline-none text-primary text-lg font-bold bg-transparent placeholder:text-faint" />
          </div>
          <p className="text-faint text-[11px] mt-1 px-1">Moved from your wallet now. Leave blank to start at ₦0.</p>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary font-semibold text-sm">Auto-save</p>
              <p className="text-muted text-xs">Automatically move money on a schedule</p>
            </div>
            <button onClick={() => setAutoSaveOn(v => !v)}
              className="w-12 h-7 rounded-full transition-colors relative"
              style={{ background: autoSaveOn ? "var(--color-brand)" : "var(--color-border-strong)" }}>
              <span className="absolute top-1 w-5 h-5 bg-white rounded-full transition-all" style={{ left: autoSaveOn ? "1.5rem" : "0.25rem" }} />
            </button>
          </div>
          {autoSaveOn && (
            <div className="mt-3 space-y-2">
              <div
                className="bg-surface-muted border rounded-xl flex items-center px-3 transition-colors"
                style={{ borderColor: autoSaveAmountMissing ? "var(--color-warning-border)" : "var(--color-border-strong)" }}
              >
                <span className="text-muted font-bold mr-2">₦</span>
                <input type="number" value={autoAmount} onChange={e => setAutoAmount(e.target.value)} placeholder="Amount per cycle"
                  className="flex-1 py-2.5 outline-none text-primary text-sm bg-transparent placeholder:text-faint" />
              </div>
              {/* FIXED — was possible to leave amount empty/0 and still
                  create the goal with autoSave.enabled:true, silently
                  producing a goal that looks automated but moves
                  nothing — exactly the state found in real data. */}
              {autoSaveAmountMissing && (
                <p className="text-[11px] flex items-center gap-1" style={{ color: "var(--color-warning-text)" }}>
                  <i className="ri-error-warning-line" />Set an amount or auto-save won't actually move any money.
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                {["daily", "weekly", "monthly"].map(f => (
                  <button key={f} onClick={() => setAutoFreq(f)}
                    className="py-2 rounded-xl text-xs font-semibold capitalize transition-all"
                    style={{
                      background: autoFreq === f ? "var(--color-brand)" : "var(--color-surface-hover)",
                      color:      autoFreq === f ? "#fff" : "var(--color-text-muted)",
                    }}>{f}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button onClick={handleCreate} disabled={loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Create Goal"}
        </button>
      </div>
    </div>
  );
}
